import { useEffect, useState } from 'react';
import type { Session } from '../App';
import { Container, PageHeader } from '../App';

interface Props { session: Session; }

interface EconomySnapshot {
  total_faucets: Record<string, number>;
  total_sinks: Record<string, number>;
  net_flow: Record<string, number>;
}

interface AnomalyAlert {
  alert_type: string;
  description: string;
  severity: string;
}

interface DashboardView {
  economy: EconomySnapshot;
  recent_alerts: AnomalyAlert[];
  top_events: [string, number][];
}

export default function Analytics({ session }: Props) {
  const [dashboard, setDashboard] = useState<DashboardView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Record form
  const [eventType, setEventType] = useState('quest_reward');
  const [resource, setResource] = useState('gold');
  const [amount, setAmount] = useState('100');
  const [direction, setDirection] = useState('faucet');

  const base = `/api/v1/games/${session.gameId}/modules/analytics`;

  const reload = () => {
    fetch(`${base}/dashboard`)
      .then(r => r.json())
      .then((d: { dashboard: DashboardView }) => setDashboard(d.dashboard))
      .catch(e => setError(e.message));
  };

  useEffect(reload, [session.gameId]);

  const recordEvent = async () => {
    if (!eventType.trim() || !resource.trim()) return;
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${base}/record`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_type: eventType.trim(),
          resource: resource.trim(),
          amount: Number(amount),
          direction,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      reload();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const allResources = dashboard
    ? [...new Set([
        ...Object.keys(dashboard.economy.total_faucets),
        ...Object.keys(dashboard.economy.total_sinks),
      ])]
    : [];

  return (
    <Container>
      <PageHeader
        title="Analytics"
        subtitle="Economy health, faucet/sink tracking, and anomaly detection."
      />
      {error && <div className="error-banner">{error}</div>}

      {/* Economy snapshot */}
      {dashboard && allResources.length > 0 && (
        <div className="card mb-5">
          <h3 className="text-sm font-semibold p-4 pb-2">Economy Snapshot</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Resource</th><th>Faucets</th><th>Sinks</th><th>Net Flow</th></tr>
              </thead>
              <tbody>
                {allResources.map(r => {
                  const faucet = dashboard.economy.total_faucets[r] ?? 0;
                  const sink = dashboard.economy.total_sinks[r] ?? 0;
                  const net = dashboard.economy.net_flow[r] ?? 0;
                  return (
                    <tr key={r}>
                      <td className="font-mono text-xs">{r}</td>
                      <td className="text-green-400">+{faucet.toFixed(1)}</td>
                      <td className="text-red-400">-{sink.toFixed(1)}</td>
                      <td className={net >= 0 ? 'text-green-400' : 'text-red-400'}>{net >= 0 ? '+' : ''}{net.toFixed(1)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent alerts */}
      {dashboard && dashboard.recent_alerts.length > 0 && (
        <div className="card mb-5">
          <h3 className="text-sm font-semibold p-4 pb-2">Recent Alerts</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Type</th><th>Severity</th><th>Description</th></tr>
              </thead>
              <tbody>
                {dashboard.recent_alerts.map((a, i) => (
                  <tr key={i}>
                    <td className="font-mono text-xs">{a.alert_type}</td>
                    <td>
                      <span className={`badge ${a.severity === 'critical' ? 'badge-warn' : 'badge-muted'}`}>
                        {a.severity}
                      </span>
                    </td>
                    <td className="text-xs">{a.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top events */}
      {dashboard && dashboard.top_events.length > 0 && (
        <div className="card mb-5 p-4">
          <h3 className="text-sm font-semibold mb-3">Top Events</h3>
          <div className="flex gap-3 flex-wrap">
            {dashboard.top_events.map(([name, count]) => (
              <div key={name} className="card p-2 text-xs">
                <div className="font-mono">{name}</div>
                <div className="text-fg-muted">{count} occurrences</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Record event */}
      <div className="card p-4">
        <h3 className="text-sm font-semibold mb-3">Record Event</h3>
        <div className="flex gap-3 items-end flex-wrap">
          <div>
            <label className="text-xs text-fg-muted block mb-1">Event Type</label>
            <input className="input" value={eventType} onChange={e => setEventType(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-fg-muted block mb-1">Resource</label>
            <input className="input" value={resource} onChange={e => setResource(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-fg-muted block mb-1">Amount</label>
            <input className="input w-24" type="number" value={amount} onChange={e => setAmount(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-fg-muted block mb-1">Direction</label>
            <select className="input" value={direction} onChange={e => setDirection(e.target.value)}>
              <option value="faucet">Faucet</option>
              <option value="sink">Sink</option>
            </select>
          </div>
          <button className="btn btn-primary" disabled={busy || !eventType.trim() || !resource.trim()} onClick={recordEvent}>Record</button>
        </div>
      </div>

      {!dashboard && (
        <div className="empty mt-5">
          <div className="text-fg-2 font-medium mb-1">No analytics data yet</div>
          <div className="text-xs text-fg-muted">Record events above to see the dashboard.</div>
        </div>
      )}
    </Container>
  );
}
